import React, { useState } from 'react';
import Skeleton from 'react-loading-skeleton';
import CheckoutPriceDetails from '@/components/complete/CheckoutPriceDetails';
import CheckoutSecondaryPriceDetails from '@/components/complete/CheckoutSecondaryPriceDetails';
import { useClaim } from '@/hooks/mainStore';
import { executeStep, restartFlow } from '@/lib/transactionsFlow/utils';
import useMainStore from '@/store/mainStore';
import { StepComponent } from './StepComponent';

interface TransactionsFlowProps {
  onSuccess: () => Promise<void>;
}

export default function TransactionsFlow({ onSuccess }: TransactionsFlowProps) {
  // Business data from store
  const store = useMainStore();
  const studioInstance = useMainStore((state) => state.studioInstance);
  const numberOfMints = useMainStore((state) => state.numberOfMints);
  const reservoirFloorToken = useMainStore((state) => state.reservoirFloorToken);
  const txSteps = useMainStore((state) => state.txSteps);

  // hooks
  const { refreshClaim } = useClaim();
  // component state
  const [isProcessing, setIsProcessing] = useState(false);
  const [retryAttempts, setRetryAttempts] = useState<Record<number, number>>({});

  const handleStepExecution = async (stepIndex: number) => {
    if (isProcessing) return;
    if (!studioInstance) throw new Error('Studio instance not found');

    setIsProcessing(true);

    try {
      await executeStep(store, stepIndex);

      // Reset retry attempts on success
      setRetryAttempts((prev) => ({ ...prev, [stepIndex]: 0 }));

      // Check if this was the last step
      if (stepIndex === txSteps.length - 1) {
        await handleSuccess();
      }
    } catch (error) {
      refreshClaim();

      // Increment retry attempts  (they get 2 retry attempts before showing "contact support" button)
      setRetryAttempts((prev) => ({
        ...prev,
        [stepIndex]: (prev[stepIndex] || 0) + 1,
      }));
    } finally {
      setIsProcessing(false);
    }
  };

  // Function to handle successful completion of all steps
  const handleSuccess = async () => {
    // Refresh the claim data to update the total minted count etc
    await refreshClaim();
    await onSuccess();
  };

  const handleRestart = async () => {
    window.ManifoldEthereumProvider.disconnect();

    setRetryAttempts({});
    await restartFlow(store);
  };

  // Show loading state while steps are being initialized
  if (!txSteps.length) {
    return (
      <div className="m-claim--flex m-claim--size-full m-claim--flex-col m-claim--gap-4">
        <div className="m-claim--text-xl m-claim--font-bold m-claim--text-primary">Mint</div>
        <div className="m-claim--flex m-claim--flex-col m-claim--gap-6">
          <Skeleton
            height={80}
            count={3}
          />
        </div>
      </div>
    );
  }

  const assetName = studioInstance?.publicData.asset?.name || 'Token';

  return (
    <div className="m-claim--flex m-claim--size-full m-claim--flex-col">
      {/* Fixed Header */}
      <div className="m-claim--mb-4 m-claim--shrink-0 m-claim--text-xl m-claim--font-bold m-claim--text-primary">
        Checkout
      </div>

      {/* Scrollable Transaction Steps - Takes remaining space */}
      <div className="m-claim--grow m-claim--overflow-y-auto">
        <div className="m-claim--flex m-claim--flex-col m-claim--gap-6">
          {txSteps.map((step, index) => (
            <StepComponent
              key={step.config.id}
              step={step}
              isProcessing={isProcessing}
              retryAttempts={retryAttempts[index] || 0}
              updateStepState={(updates) => store.updateTxStepState(index, updates)}
              onExecute={() => handleStepExecution(index)}
              onRestart={handleRestart}
            />
          ))}
        </div>
      </div>

      {/* Fixed Warning Footer */}
      <div className="m-claim--mt-4 m-claim--flex m-claim--shrink-0 m-claim--flex-col m-claim--gap-1 m-claim--rounded-md m-claim--bg-surface-secondary m-claim--p-4">
        <div className="m-claim--grid m-claim--grid-cols-2 m-claim--gap-1 m-claim--text-sm">
          <span className="m-claim--text-ellipsis m-claim--text-sm m-claim--text-primary">
            {assetName}
          </span>
          <span className="m-claim--text-right m-claim--text-sm m-claim--text-primary">
            {numberOfMints}x
          </span>
        </div>

        {reservoirFloorToken ? <CheckoutSecondaryPriceDetails /> : <CheckoutPriceDetails />}
      </div>
    </div>
  );
}
